import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import VideoEditingService, {
  ClipCategory, ClipLabelFacets, ClipLabelVocabulary, CreationSource,
  LibraryClipLabel, LibraryFilters, LibraryMode,
} from 'services/VideoEditingService';

const videoEditingService = new VideoEditingService();

/**
 * Searching the b-roll library: filters, facet counts, paging and the vocabularies.
 *
 * Shared by the library page and the video editor's b-roll picker. The two show results very
 * differently — one has Edit/Remove per card, the other "Add to project" — so only the search
 * itself is shared, not the presentation. Splitting it here rather than at a whole-component
 * boundary keeps the part that is genuinely subtle in one place: the facet counts must be
 * computed over exactly the filters the search ran, because the server excludes each facet's own
 * dimension to keep its options switchable and can only do that if it's told the rest.
 */
export interface BrollSearchOptions {
  // Pre-selects the product, e.g. from the edit project's own product category.
  initialProductCategory?: string | null;
  pageSize?: number;
  mode?: LibraryMode;
}

export function useBrollSearch({
  initialProductCategory = null,
  pageSize: initialPageSize = 25,
  mode: initialMode = 'broll',
}: BrollSearchOptions = {}) {
  const [labels, setLabels] = useState<LibraryClipLabel[]>([]);
  const [facets, setFacets] = useState<ClipLabelFacets | null>(null);
  const [vocabulary, setVocabulary] = useState<ClipLabelVocabulary | null>(null);
  const [categoryVocab, setCategoryVocab] = useState<ClipCategory[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(initialPageSize);
  const [mode, setMode] = useState<LibraryMode>(initialMode);

  const [productCategory, setProductCategory] = useState<string | null>(initialProductCategory);
  const [tags, setTags] = useState<string[]>([]);
  const [categories, setCategories] = useState<string[]>([]);
  const [creationSource, setCreationSource] = useState<CreationSource | null>(null);
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  // Bumped per search so a slow response can be recognised as superseded (see `search`).
  const requestSeq = useRef(0);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedQuery(query), 300);
    return () => clearTimeout(timer);
  }, [query]);

  useEffect(() => {
    videoEditingService.getClipLabelVocabulary().then(v => { if (v) setVocabulary(v); });
    videoEditingService.listClipCategories().then(r => { if (r) setCategoryVocab(r.categories); });
  }, []);

  // Everything currently narrowing the library, in one object so the search and the facet
  // counts cannot be given different filters.
  //
  // `clipType` is deliberately absent: the model's Type classification turned out not to
  // describe anything an editor searches by, so it is no longer offered. The column and the
  // b-roll talking rule still use it server-side.
  const filters: LibraryFilters = useMemo(() => ({
    mode,
    productCategory: productCategory || undefined,
    tags,
    categories,
    creationSource: creationSource || undefined,
    q: debouncedQuery || undefined,
  }), [mode, productCategory, tags, categories, creationSource, debouncedQuery]);

  useEffect(() => {
    videoEditingService.getClipLabelFacets(filters).then(f => { if (f) setFacets(f); });
  }, [filters]);

  // The product used to be mandatory before anything was fetched, on the reasoning that "every
  // clip we own" is not a useful starting point. In practice it was: someone pulling b-roll into
  // an edit is often looking for a shot ("hands", "morning light") that spans products, and
  // being made to guess a product first meant they couldn't look at all. Results are paged, so
  // an unfiltered search costs one page like any other.
  const search = useCallback(async () => {
    // Page through quickly and several searches are in flight at once. They can come back in any
    // order, so results are only applied if nothing newer has started — otherwise the grid ends
    // up showing whichever request happened to finish last, which is not necessarily the page
    // you are on. A superseded response also leaves `loading` alone: the newer one owns it.
    const seq = ++requestSeq.current;
    setLoading(true);
    const resp = await videoEditingService.searchClipLabels({
      ...filters,
      limit: pageSize,
      offset: page * pageSize,
    });
    if (seq !== requestSeq.current) return;
    setLabels(resp?.labels || []);
    setTotal(resp?.total || 0);
    setLoading(false);
  }, [filters, page, pageSize]);

  useEffect(() => { search(); }, [search]);

  // Any change to what's being searched invalidates the page number — staying on page 4 of a
  // narrower result set would show an empty grid.
  useEffect(() => { setPage(0); }, [filters, pageSize]);

  const refreshFacets = useCallback(() => {
    videoEditingService.getClipLabelFacets(filters).then(f => { if (f) setFacets(f); });
  }, [filters]);

  // Create a category and fold it into the vocabulary immediately, so it is selectable in the
  // editor AND offered as a filter without a reload.
  const createCategory = useCallback(async (name: string) => {
    const { category } = await videoEditingService.createClipCategory(name);
    setCategoryVocab(prev => {
      const rest = (prev || []).filter(c => c.slug !== category.slug);
      return [...rest, category].sort((a, b) => a.name.localeCompare(b.name));
    });
    return category;
  }, []);

  // A category's display name. Falls back to the slug for one that has since been archived —
  // it's still on the clip, so the chip has to say something.
  const categoryName = useCallback(
    (slug: string) => categoryVocab?.find(c => c.slug === slug)?.name || slug,
    [categoryVocab],
  );

  // Counts the product too, now that it narrows the search like any other filter rather than
  // being the precondition for running one at all.
  const activeFilters = tags.length + categories.length + (productCategory ? 1 : 0)
    + (creationSource ? 1 : 0) + (debouncedQuery ? 1 : 0);

  function clearFilters() {
    setProductCategory(null);
    setTags([]);
    setCategories([]);
    setCreationSource(null);
    setQuery('');
  }

  return {
    // results
    labels, setLabels, total, loading, page, setPage, pageSize, setPageSize,
    // filter state
    mode, setMode, productCategory, setProductCategory, tags, setTags,
    categories, setCategories, creationSource, setCreationSource, query, setQuery,
    filters, activeFilters, clearFilters,
    // vocabularies
    facets, vocabulary, categoryVocab, categoryName, createCategory,
    // actions
    search, refreshFacets,
  };
}
